import type { CapabilityType, OpenTiledWindowRequest } from '@/types/workflow-graph';

/**
 * Known capability type prefixes for unqualified ASL resources.
 * Mirrors packages/engines/workflow/workflow/middleware/client_executor.go qualifyResource.
 */
const CAPABILITY_TYPE_PREFIXES = new Set([
    'tool', 'agent', 'model', 'prompt', 'provider', 'policy', 'schema',
    'skill', 'role', 'subscription', 'application', 'workflow',
]);

/**
 * Qualify an unqualified workflow Task Resource with the workflow app key.
 * e.g. "tool.system.lineage" + "more0ai" → "more0ai/tool.system.lineage"
 *
 * "system.registry" is left unchanged (first segment after stripping "system." is not a type).
 */
export function qualifyWorkflowResource(resource: string, appKey: string): string {
    if (!appKey) return resource;

    let name = resource;
    if (name.startsWith('system.')) {
        name = name.slice('system.'.length);
    }

    const firstSeg = name.split('.')[0] ?? '';
    if (CAPABILITY_TYPE_PREFIXES.has(firstSeg)) {
        return `${appKey}/${resource}`;
    }

    return resource;
}

/** Strip explicit ASL type prefix: "tool:app/key" → "app/key" */
export function stripResourceTypePrefix(resource: string): string {
    return resource.replace(/^(agent|tool|model|workflow):/, '');
}

/** Strip method suffix: "tool.foo::record" → "tool.foo" */
export function stripMethodSuffix(resource: string): string {
    const methodSep = resource.indexOf('::');
    return methodSep >= 0 ? resource.slice(0, methodSep) : resource;
}

export interface WorkflowResourceLookup {
    appKey?: string;
    name: string;
    fqcn?: string;
}

/**
 * Parse an ASL Task Resource into app key, capability name, and FQCN for API lookup.
 */
export function parseWorkflowResourceForLookup(
    resource: string,
    appKey?: string,
    resourceAppKey?: string,
): WorkflowResourceLookup {
    const stripped = stripMethodSuffix(stripResourceTypePrefix(resource));
    const effectiveAppKey = resourceAppKey ?? appKey;

    if (stripped.includes('/')) {
        const slashIdx = stripped.indexOf('/');
        return {
            appKey: stripped.substring(0, slashIdx),
            name: stripped.substring(slashIdx + 1),
            fqcn: stripped,
        };
    }

    const qualified = effectiveAppKey
        ? qualifyWorkflowResource(stripped, effectiveAppKey)
        : stripped;

    if (qualified.includes('/')) {
        const slashIdx = qualified.indexOf('/');
        return {
            appKey: qualified.substring(0, slashIdx),
            name: qualified.substring(slashIdx + 1),
            fqcn: qualified,
        };
    }

    return {
        appKey: effectiveAppKey,
        name: stripped,
        fqcn: effectiveAppKey ? `${effectiveAppKey}/${stripped}` : undefined,
    };
}

const ENTITY_TYPE_MAP: Record<CapabilityType, OpenTiledWindowRequest['entityType']> = {
    agent: 'agent',
    tool: 'tool',
    model: 'model',
    workflow: 'workflow',
};

/**
 * Build a tiled-window open request from a workflow Task node's resource reference.
 */
export function buildWorkflowEntityOpenRequest(
    resource: string,
    resourceType: CapabilityType | 'task',
    options: {
        appKey?: string;
        resourceAppKey?: string;
        entityName: string;
    },
): OpenTiledWindowRequest | null {
    const entityType = ENTITY_TYPE_MAP[resourceType as CapabilityType];
    if (!entityType) return null;

    const lookup = parseWorkflowResourceForLookup(
        resource,
        options.appKey,
        options.resourceAppKey,
    );

    return {
        entityType,
        entityId: lookup.fqcn ?? lookup.name,
        entityName: options.entityName,
        appKey: lookup.appKey,
    };
}
